import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { computed, DOCUMENT, effect, inject, Injectable, makeStateKey, PLATFORM_ID, REQUEST, signal, TransferState, untracked } from '@angular/core';
import { Router } from '@angular/router';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { AuthStore } from '@rpgc-ai/store/auth/auth.store';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { PersonaStore } from '@rpgc-ai/store/persona/persona.store';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ProfileStore } from '@rpgc-ai/store/profile/profile.store';

import type { IAuthSession, IUser } from '@rpgc-ai/shared-interfaces/interfaces/auth.interface';
import { IS_SERVER } from '@rpgc-ai/shared-interfaces/tokens/is-server.token';
import { WINDOW } from '@rpgc-ai/shared-interfaces/tokens/window.token';

import { AUTH_CALLBACK_QUERY_PARAM } from './auth-callback.constants';
import { authClient, authClientYandex, normalizeHeaders } from './better-auth.client';

import { ModalService } from '../modal/modal.service';
import { LanguageService } from '../translate/language.service';

export interface SessionData {
  user: IUser;
  session: IAuthSession;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let ym: any;

export interface GetSessionOptions {
  showAuthorizationErrorModal?: boolean;
}

// Ключ для TransferState
const SESSION_DATA_KEY = makeStateKey<SessionData>('sessionData');

@Injectable({ providedIn: 'root' })
export class SessionService {
  // Injections
  readonly #authStore = inject(AuthStore);
  readonly #languageService = inject(LanguageService);
  readonly #modalService = inject(ModalService);
  readonly #platformId = inject(PLATFORM_ID);
  readonly #profileStore = inject(ProfileStore);
  readonly #router = inject(Router);
  readonly #transferState = inject(TransferState);
  readonly #personaStore = inject(PersonaStore);
  readonly #window = inject(WINDOW);
  readonly #document = inject(DOCUMENT);
  readonly #isServer = inject(IS_SERVER);

  readonly #request = inject(REQUEST, { optional: true });

  // Сигналы для состояния сессии
  readonly #sessionData = signal<SessionData | null>(null);
  readonly #isLoading = signal<boolean>(false);
  readonly #error = signal<string | null>(null);

  // Вычисляемые сигналы
  readonly user = computed<IUser | null>(() => this.#sessionData()?.user ?? null);
  readonly session = computed<IAuthSession | null>(() => this.#sessionData()?.session ?? null);

  readonly loadSession = signal<boolean>(false);
  readonly loadSessionAttempt = signal<number>(0);

  // In-flight запрос для дедупликации параллельных вызовов getSession
  #inflight: Promise<SessionData | null> | null = null;
  #showAuthorizationErrorModalOnFail = false;

  readonly #LOGOUT_EVENT_KEY = 'auth_logout_event';
  readonly #AUTH_SESSION_ERROR_MODAL_NAME = 'auth-session-error';

  /**
   * Авторизован ли пользователь
   * Три состояния
   * undefined - пока не определено
   * true - авторизован
   * false - не авторизован
   */
  readonly isAuthenticated = signal<boolean | undefined>(undefined);

  constructor() {
    // Инициализируем данные из TransferState на клиенте перед запуском effects
    this.#initializeFromTransferState();

    // Автоматически загружаем сессию при инициализации
    this.#loadSessionEffect();
    this.#updateSessionEffect();
    this.#initializeStorageListener();
  }

  /**
   * Получает клиент для работы с аутентификацией
   */
  get client(): typeof authClient {
    return authClient;
  }

  /**
   * Получает клиент для работы с аутентификацией Yandex
   */
  get clientYandex(): typeof authClientYandex {
    return authClientYandex;
  }

  /**
   * Получает текущую сессию пользователя
   * @param headers - Опциональные заголовки для SSR
   */
  async getSession(headers?: Record<string, string | string[] | undefined>, options: GetSessionOptions = {}): Promise<SessionData | null> {
    const hasAuthorizationCallbackQuery = this.#hasAuthorizationCallbackQuery();

    if (options.showAuthorizationErrorModal || hasAuthorizationCallbackQuery) {
      this.#showAuthorizationErrorModalOnFail = true;
    }

    // Дедупликация: если запрос уже идёт — возвращаем тот же промис
    if (this.#inflight) {
      return this.#inflight;
    }

    this.#isLoading.set(true);
    this.#error.set(null);
    this.loadSessionAttempt.update((prev) => prev + 1);

    this.#inflight = (async () => {
      try {
        // Выполняем API запрос
        const requestHeaders = headers || (this.#request?.headers as unknown as Record<string, string>);
        const fetchOptions = requestHeaders ? { headers: normalizeHeaders(requestHeaders) } : undefined;

        const response = (await authClient.getSession({
          fetchOptions,
        })) as { data: SessionData | null };

        if (response.data) {
          this.#sessionData.set(response.data);
          this.isAuthenticated.set(true);
          this.#clearAuthorizationCallbackQuery(hasAuthorizationCallbackQuery);

          // Задаём client ID после получения сессии
          const userId = response.data.user.id;
          if (userId) {
            this.#setYandexMetrikaUserId(userId);
          }

          // На сервере сохраняем в TransferState для передачи на клиент
          if (isPlatformServer(this.#platformId)) {
            this.#transferState.set(SESSION_DATA_KEY, response.data);
          }

          return response.data;
        } else {
          this.#sessionData.set(null);
          this.isAuthenticated.set(false);
          this.#clearAuthorizationCallbackQuery(hasAuthorizationCallbackQuery);
          return null;
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Ошибка при получении сессии';
        this.#error.set(errorMessage);
        this.#sessionData.set(null);
        this.isAuthenticated.set(false);

        if (this.#showAuthorizationErrorModalOnFail) {
          this.#openAuthorizationErrorModal(hasAuthorizationCallbackQuery);
        }

        return null;
      } finally {
        this.#isLoading.set(false);
        this.#inflight = null;
        this.#showAuthorizationErrorModalOnFail = false;
      }
    })();

    return this.#inflight;
  }

  /**
   * Обновляет сессию (refetch)
   */
  async refreshSession(): Promise<SessionData | null> {
    return this.getSession();
  }

  /**
   * Очищает данные сессии (например, при выходе)
   */
  clearSession(options: { notifyOtherTabs?: boolean; redirectToHome?: boolean } = {}): void {
    const { notifyOtherTabs = false, redirectToHome = false } = options;

    this.#sessionData.set(null);
    this.#error.set(null);
    this.#authStore.clearSession();
    this.#authStore.clearUser();
    this.#profileStore.clearProfile();
    this.#personaStore.clear();
    this.isAuthenticated.set(false);
    this.#document.cookie = 'utm_params=; path=/; max-age=0';
    // Также очищаем TransferState если он еще есть
    if (this.#transferState.hasKey(SESSION_DATA_KEY)) {
      this.#transferState.remove(SESSION_DATA_KEY);
    }
    // Уведомляем другие вкладки если нужно
    if (notifyOtherTabs && isPlatformBrowser(this.#platformId)) {
      this.#notifyLogout();
    }

    if (redirectToHome && isPlatformBrowser(this.#platformId)) {
      const lang = this.#languageService.getCurrentLanguage();
      this.#router.navigate([lang]);
    }
  }

  /**
   * Проверяет, истекла ли сессия
   */
  isSessionExpired(): boolean {
    const session = this.#sessionData()?.session;
    if (!session) {
      return true;
    }

    return new Date() > new Date(session.expiresAt);
  }

  /**
   * Отзывает все остальные сессии пользователя
   */
  async revokeOtherSessions(): Promise<void> {
    try {
      await authClient.revokeOtherSessions();
      // После отзыва других сессий обновляем текущую
      await this.refreshSession();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Ошибка при отзыве сессий';
      this.#error.set(errorMessage);
      throw error;
    }
  }

  /**
   * Инициализирует данные сессии из TransferState на клиенте
   */
  #initializeFromTransferState(): void {
    if (isPlatformBrowser(this.#platformId)) {
      const cachedSession = this.#transferState.get(SESSION_DATA_KEY, null);

      if (cachedSession) {
        this.#sessionData.set(cachedSession);
        this.isAuthenticated.set(true);

        // Удаляем из TransferState после использования
        this.#transferState.remove(SESSION_DATA_KEY);

        const userId = cachedSession.user.id;
        if (userId) {
          this.#setYandexMetrikaUserId(userId);
        }
      }
    }
  }

  /**
   * Инициализирует слушатель событий localStorage для синхронизации между вкладками
   */
  #initializeStorageListener(): void {
    if (isPlatformBrowser(this.#platformId)) {
      window.addEventListener('storage', (event) => {
        if (event.key === this.#LOGOUT_EVENT_KEY && event.newValue) {
          // Другая вкладка сделала logout - очищаем без уведомления
          this.clearSession({ redirectToHome: true });
        }
      });
    }
  }

  /**
   * Уведомляет другие вкладки о выходе из аккаунта
   */
  #notifyLogout(): void {
    localStorage.setItem(this.#LOGOUT_EVENT_KEY, Date.now().toString());
    // Очищаем через секунду
    setTimeout(() => {
      localStorage.removeItem(this.#LOGOUT_EVENT_KEY);
    }, 1000);
  }

  #hasAuthorizationCallbackQuery(): boolean {
    const { queryParams } = this.#router.parseUrl(this.#router.url);

    if (Object.prototype.hasOwnProperty.call(queryParams, AUTH_CALLBACK_QUERY_PARAM)) {
      return true;
    }

    const search = this.#window?.location.search;
    if (!search) {
      return false;
    }

    return new URLSearchParams(search).has(AUTH_CALLBACK_QUERY_PARAM);
  }

  #clearAuthorizationCallbackQuery(shouldClear: boolean): void {
    if (!shouldClear) {
      return;
    }

    const urlTree = this.#router.parseUrl(this.#router.url);
    if (!Object.prototype.hasOwnProperty.call(urlTree.queryParams, AUTH_CALLBACK_QUERY_PARAM)) {
      const href = this.#window?.location.href;
      if (!href) {
        return;
      }

      const url = new URL(href);
      if (!url.searchParams.has(AUTH_CALLBACK_QUERY_PARAM)) {
        return;
      }

      url.searchParams.delete(AUTH_CALLBACK_QUERY_PARAM);

      const nextUrl = `${url.pathname}${url.search}${url.hash}`;
      this.#window.history.replaceState(this.#window.history.state, '', nextUrl);
      return;
    }

    const nextQueryParams = { ...urlTree.queryParams };
    delete nextQueryParams[AUTH_CALLBACK_QUERY_PARAM];
    urlTree.queryParams = nextQueryParams;

    void this.#router.navigateByUrl(urlTree, { replaceUrl: true });
  }

  #openAuthorizationErrorModal(shouldClearAuthorizationCallbackQuery: boolean): void {
    if (!isPlatformBrowser(this.#platformId)) {
      return;
    }

    const modalOutlet = this.#router.parseUrl(this.#router.url).root.children['modal'];
    const currentModal = modalOutlet?.segments.map((segment) => segment.path).join('/');

    if (currentModal === this.#AUTH_SESSION_ERROR_MODAL_NAME) {
      this.#clearAuthorizationCallbackQuery(shouldClearAuthorizationCallbackQuery);
      return;
    }

    this.#modalService.closeModal(true, undefined, [this.#AUTH_SESSION_ERROR_MODAL_NAME], {
      queryParams: shouldClearAuthorizationCallbackQuery ? { [AUTH_CALLBACK_QUERY_PARAM]: null } : undefined,
      queryParamsHandling: shouldClearAuthorizationCallbackQuery ? 'merge' : undefined,
      replaceUrl: shouldClearAuthorizationCallbackQuery,
    });
  }

  /**
   * Эффект обновления сессии
   */
  #updateSessionEffect(): void {
    effect(() => {
      const sessionData = this.#sessionData();
      if (!sessionData) {
        return;
      }

      const { user, session } = sessionData;

      untracked(() => {
        this.#authStore.setUser(user);
        this.#authStore.setSession(session);
      });
    });
  }

  /**
   * Effect для автоматической загрузки сессии
   */
  #loadSessionEffect(): void {
    effect(() => {
      const loadSession = this.loadSession();
      const hasUser = !!this.#authStore.user()?.id;
      const hasSessionData = !!this.#sessionData();
      const isLoading = this.#isLoading();
      const isAuthenticated = this.isAuthenticated();

      // Загружаем сессию если:
      // - запрошена загрузка
      // - нет данных сессии (в том числе из TransferState)
      // - нет активной загрузки
      // - нет пользователя в сторе
      // - статус аутентификации еще не определен (undefined) или не проверялся
      if (loadSession && !hasSessionData && !isLoading && !hasUser && isAuthenticated !== false) {
        untracked(() => {
          this.getSession();
        });
      }
    });
  }

  #setYandexMetrikaUserId(userId: string): void {
    if (this.#isServer || typeof ym === 'undefined') {
      return;
    }
    ym(105383140, 'userParams', { UserID: userId });
    ym(105383140, 'setUserID', userId);
  }
}
